/**
 * 权限控制：基于后端返回的权限码列表 + is_super_admin。
 *
 * 后端 me 接口返回：
 *   - permissions: string[]  已授权权限码（超管为 ['*']）
 *   - is_super_admin: boolean
 *
 * 每个页面路由的 access 字段对应一个 visitXxx 布尔。
 * 与后端 PermissionCode enum 保持同步（16 个页面权限点 + 2 个管理权限点）。
 *
 * @see https://umijs.org/docs/max/access#access
 */
export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const perms: string[] = currentUser?.permissions ?? [];
  const isSuper = currentUser?.is_super_admin ?? false;

  // 超管或拥有对应权限码即放行
  const can = (code: string) => isSuper || perms.includes(code) || perms.includes('*');

  return {
    // 页面级（与 config/routes.ts access 字段一一对应）
    visitGameSettlement: can('game_settlement'),
    visitActivityRecord: can('activity_record_adjust_log'),
    visitClientDebug: can('client_debug'),
    visitOnlineUsers: can('online_users'),
    visitMatchMonitor: can('match_monitor'),
    visitMatchQueue: can('match_queue'),
    visitMatchAnalytics: can('match_analytics'),
    visitMatchDiagnostics: can('match_diagnostics'),
    visitMatchConfig: can('match_config'),
    // 匹配系统父菜单：任一 match 子权限可见即显示（避免只授了某个子权限时父菜单被隐藏）
    visitMatch:
      can('match_monitor') ||
      can('match_queue') ||
      can('match_analytics') ||
      can('match_diagnostics') ||
      can('match_config'),
    visitRedis: can('redis'),
    visitSls: can('sls'),
    visitRustParser: can('test_rust_parser'),
    // 系统维护（父菜单：任一子页有权限即显示）
    visitMaintenanceLeaderboardSync: can('maintenance_leaderboard_sync'),
    visitMaintenanceServiceOps: can('maintenance_service_ops'),
    visitMaintenanceLeagueQualify: can('maintenance_league_qualify'),
    visitMaintenanceDbCleanup: can('maintenance_db_cleanup'),
    visitMaintenanceSeasonSwitch: can('maintenance_season_switch'),
    visitMaintenance:
      can('maintenance_leaderboard_sync') ||
      can('maintenance_service_ops') ||
      can('maintenance_league_qualify') ||
      can('maintenance_db_cleanup') ||
      can('maintenance_season_switch'),
    // 自助功能：登录即可访问（查看/申请自己的权限），不依赖权限点——
    // 否则没该权限的普通用户进不去申请页，形成死循环
    visitMyPermissions: true,
    // 管理功能
    visitUserManagement: can('user_management'),
    // 「设置」一级目录：含"我的权限"(登录即可见)，对所有登录用户显示
    visitSettings: true,
    // 「其它」一级目录：目前仅 RustDebug 解析器
    visitOthers: can('test_rust_parser'),
    // 审计查询页：仅超管（后端 list_audit_logs 校验 is_super_admin，不依赖权限码）
    visitAudit: isSuper,
    // 超管总开关（供页面内判断）
    isSuperAdmin: isSuper,
  };
}